import React, { useState, useEffect, useCallback } from "react";
import { matchPath } from "react-router";
import { useLocation, useHistory } from "react-router-dom";

import Loader from "../../components/loader/Loader";
import { NoProjectErrorMessageBox, CallMegazoneErrorMessageBox } from "./ErrorMessageBox";

import routes from "../../cores/routes";
import { PUBLIC_URL } from "../../constants";
import { parseQueryParameter } from "../../cores";

import equal from "deep-equal";

import { sessionKeeper } from "@cores/sessionKeeper";
import compareVersions, { VERSION_CM_5112_PROJECT_SITE_ACCESS } from "../../cores/version";

import Loadable from "@react-loadable/revised";
import menuService from "@services/menuService";
import {
    Avatar,
    Box,
    Grid,
    IconButton,
    InputAdornment,
    List,
    ListItem,
    Popper,
    ClickAwayListener,
    Paper,
    MenuList,
    MenuItem,
    Tooltip,
} from "@mzc-pdc/ui";
import { useDispatch, useSelector } from "react-redux";
import { Trans, useTranslation } from "react-i18next";
import { IconCheckDefault, IconChevronRight, IconMoreHoriz, IconSearch, IconSetting } from "@mzc-cloudplex/icons";
import TextField from "@components_v2/text-field/text-field";
import theme from "@styles/theme";
import i18n from "@lib/i18n";
import { setStage } from "@modules/stage";
import { setProject } from "@modules/project";
import Typography from "@components_v2/typography/typography";
import { siteMode } from "@cores/siteMode";
import Cookies from "js-cookie";
import { alpha } from "@mui/material";
import { getProjectRandomColor } from "@utils/get-random-color";

const LoadableTokenExpiredModal = Loadable({
    loader: () => import("../../components/modals/TokenExpired"),
    loading: () => null,
});

const Stage = (props) => {
    const { t } = useTranslation();
    const history = useHistory();
    const location = useLocation();
    const dispatch = useDispatch();

    const params = parseQueryParameter(location.search);
    const paramRedirectUrl = params.get("redirectUrl");
    const [anchorEl, setAnchorEl] = useState(null);
    const [accessibleStages, setAccessibleStages] = useState(null);
    const [hasCheckedAllStages, setHasCheckedAllStages] = useState(false);
    const [showLoading, setShowLoading] = useState(true);
    const [showNoSiteAccess, setShowNoSiteAccess] = useState(false);
    const [componentUnmounted, setComponentUnmounted] = useState(false);
    const [projectFilterKeyword, setProjectFilterKeyword] = useState({});

    const user = useSelector(({ user }) => user);
    const stages = useSelector(({ user }) => user.stages.data);
    const global = useSelector(({ global }) => global);

    const checkStages = useCallback(
        (stages) => {
            if (componentUnmounted) {
                return;
            }
            const accessible = [];
            let stagesCount = stages ? stages.length : 0;
            let checkedCount = 0;
            for (let i = 0; i < stagesCount; i++) {
                const stage = stages[i];
                if (stage.checked) checkedCount++;
                if (stage.isAccessible) accessible.push(stage);
            }
            const hasCheckedAllStages = stagesCount === 0 || checkedCount === stagesCount;
            const showLoading = !stages || checkedCount === 0;
            const shouldUpdateState =
                showLoading !== showLoading ||
                hasCheckedAllStages !== hasCheckedAllStages ||
                !equal(accessible, accessibleStages);

            if (shouldUpdateState) {
                setShowLoading(showLoading);
                setHasCheckedAllStages(hasCheckedAllStages);
                setAccessibleStages(accessible);
            }
        },
        [componentUnmounted],
    );

    const onClickAdmin = async (stage) => {
        const project = stage.projects && stage.projects[0];
        if (project) {
            await enterToApp(stage, project, true);
        }
    };

    const onClickProject = async (stage, project) => {
        await enterToApp(stage, project);
    };

    const onClickToggleActionButton = (event, projectId) => {
        if (anchorEl && anchorEl.projectId === projectId) {
            setAnchorEl(null);
        } else {
            const element = event.currentTarget;
            element.projectId = projectId;
            setAnchorEl(element);
        }
    };

    const handleClosePopper = () => {
        setAnchorEl(null);
    };

    const getCurrentProjectId = () => {
        return anchorEl?.projectId || null;
    };

    const onClickSetDefaultProject = (stageId, projectId) => {
        const key = `mz.mcm-${user.data.id}`;
        const userDataString = localStorage.getItem(key);
        let userDataJson = {};
        if (userDataString) {
            userDataJson = JSON.parse(userDataString);
        }

        localStorage.setItem(
            key,
            JSON.stringify({
                ...userDataJson,
                [stageId]: projectId,
            }),
        );

        handleClosePopper();
    };

    const onClickUnSetDefaultProject = (stageId) => {
        const key = `mz.mcm-${user.data.id}`;
        const userDataString = localStorage.getItem(key);
        let userDataJson = {};
        if (userDataString) {
            userDataJson = JSON.parse(userDataString);
        }
        delete userDataJson[stageId];

        if (Object.keys(userDataJson).length === 0) {
            localStorage.removeItem(key);
        } else {
            localStorage.setItem(
                key,
                JSON.stringify({
                    ...userDataJson,
                }),
            );
        }

        handleClosePopper();
    };

    const enterToApp = async (stage, project, isAdminApp) => {
        const setStagePromise = dispatch(setStage(stage));
        const setProjectPromise = dispatch(setProject(project));
        await Promise.all([setStagePromise, setProjectPromise]);
        if (isAdminApp) {
            history.push(menuService.paths.admin.users(stage.id));
            return;
        }
        const currentSiteMode = siteMode.get();
        const targetPath = currentSiteMode === "CONSOLE" ? "/" : "/library/";
        history.push(targetPath);
    };

    const getRedirectPath = (stage, project, isAdminApp) => {
        if (isPreviousSelectedProject(stage, project, isAdminApp)) {
            let url = paramRedirectUrl || window.location.origin;

            let redirectPath = "/";
            routes.some((route) => {
                const match = matchPath(url.replace(window.location.origin, ""), {
                    path: route.path,
                    exact: true,
                });

                if (match && route.redirectPath) {
                    redirectPath = route.redirectPath;
                }

                return match;
            });

            return redirectPath;
        }

        return isAdminApp ? "/admin/space/users" : "/dashboard";
    };

    const getSessionCookie = (key) => {
        const sessionCookie = sessionKeeper.get();
        if (sessionCookie) {
            return key ? sessionCookie[key] : sessionCookie;
        }
        return null;
    };

    const getStoredUserData = (key) => {
        const storedUserData = localStorage.getItem(`mz.mcm-${user.data?.id}`);
        if (storedUserData) {
            const result = JSON.parse(storedUserData);
            return key ? result[key] : result;
        }

        return null;
    };

    const isPreviousSelectedProject = (stage, project, isAdminApp) => {
        const cookie = getSessionCookie();
        if (!cookie) {
            return false;
        }

        const redirectUrl = paramRedirectUrl;
        const isEqualPreviousAppMode =
            !redirectUrl ||
            (isAdminApp && redirectUrl.indexOf("/admin/") > -1) ||
            (!isAdminApp && redirectUrl.indexOf("/admin/") < 0);

        return cookie.stageId === stage.id && cookie.projectId === project.id && isEqualPreviousAppMode;
    };

    const filterActiveProject = useCallback(
        (stageId, projects) => {
            const key = `mz.mcm-${user.data.id}`;
            const userDataString = localStorage.getItem(key);

            const userDataJson = userDataString ? JSON.parse(userDataString) : {};
            const defaultProjectId = userDataJson[stageId];

            return (
                (projects &&
                    projects
                        .sort((prevPrj, nextPrj) => {
                            if (prevPrj.id === defaultProjectId) return -1;
                            if (nextPrj.id === defaultProjectId) return 1;
                            return 0;
                        })
                        .filter(
                            (v) =>
                                v.isActive &&
                                v.name?.toLowerCase().includes(projectFilterKeyword[stageId]?.toLowerCase() ?? ""),
                        )) ||
                []
            );
        },
        [user?.data?.id, projectFilterKeyword],
    );

    const showNoAccessPopup = () => {
        setShowNoSiteAccess(true);
    };

    const closeNoAccessPopup = () => {
        setShowNoSiteAccess(false);
    };

    const onHandleChangeFilterKeyword = (stageId, keyword) => {
        setProjectFilterKeyword({
            ...projectFilterKeyword,
            [stageId]: keyword,
        });
    };

    useEffect(async () => {
        if (!stages) return;
        checkStages(stages);

        if (document.documentElement.classList.contains("utils-menu-open"))
            document.documentElement.classList.remove("utils-menu-open");
        if (global.isAgreementRequired) return;

        if (hasCheckedAllStages) {
            const domain = sessionKeeper.getDomain();
            const entryTarget = Cookies.get("entryTarget", {
                domain,
            });
            if (entryTarget) {
                try {
                    const { spaceId, projectId } = JSON.parse(entryTarget);
                    const stage = stages.find((s) => s.id === spaceId);
                    if (stage) {
                        const project = stage.projects.find((p) => p.id === projectId);
                        if (project) {
                            await enterToApp(stage, project, false);
                        }
                    }
                } catch (e) {
                    console.error(`Failed to enter`, e);
                } finally {
                    Cookies.remove("entryTarget", { path: "/", domain });
                }
            } else {
                // 접근 가능한 프로젝트가 1개인 스페이스가 딱 1개면 바로 진입되도록 적용함.
                const accessibleStages = stages?.filter((s) => s.user?.projects?.length > 0) ?? [];
                const oneProjectStages = accessibleStages.filter((s) => s.user?.projects?.length === 1);
                if (accessibleStages.length === 1 && oneProjectStages.length === 1) {
                    const project = oneProjectStages[0].user.projects[0];
                    if (project) {
                        await enterToApp(oneProjectStages[0], project, false);
                    }
                }
            }
        }
        return () => {
            setComponentUnmounted(true);
        };
    }, [stages, hasCheckedAllStages]);

    useEffect(() => {
        //Note: 1.0.98 이전 defaultProject는 하나만 지정가능했는데 이걸 스페이스 별 1개로 구조를 변경하면서 기존 defaultStage/ProjectId가 있는 경우 구조를 변경해서 동일 기능을 제공하기 위해 추가했습니다.
        const key = `mz.mcm-${user.data.id}`;
        const userDataString = localStorage.getItem(key);
        let userDataJson = {};
        if (userDataString) {
            userDataJson = JSON.parse(userDataString);
        }
        if (!!userDataJson["defaultProjectId"] && !!userDataJson["defaultStageId"]) {
            const { defaultStageId, defaultProjectId, ...rest } = userDataJson;
            localStorage.setItem(key, JSON.stringify({ ...rest, [defaultStageId]: defaultProjectId }));
        }
    }, []);

    useEffect(() => {
        // html 요소의 overflow를 임시로 조정하여 스크롤 활성화
        const originalHtmlOverflow = document.documentElement.style.overflow;
        const originalHtmlOverflowY = document.documentElement.style.overflowY;
        document.documentElement.style.overflowY = "auto";

        return () => {
            // 컴포넌트 언마운트 시 원래 상태로 복원
            document.documentElement.style.overflow = originalHtmlOverflow;
            document.documentElement.style.overflowY = originalHtmlOverflowY;
        };
    }, []);

    return (
        <React.Fragment>
            {showLoading && <Loader />}

            {showNoSiteAccess && (
                <LoadableTokenExpiredModal error={{ type: "NO_SITE_ACCESS" }} onConfirm={closeNoAccessPopup} />
            )}

            {accessibleStages && accessibleStages.length > 0 ? (
                <div className={"gate"} style={{ paddingTop: "56px" }}>
                    <div className={"message"}>
                        <Box>
                            <Typography variant="h2" color={theme.palette.greySecondary.main}>
                                {t(`common::msg::${"Hi, {{name}}"}`, {
                                    name: user.data.name,
                                })}
                            </Typography>
                        </Box>
                        <Box sx={{ mt: theme.spacing(1) }}>
                            <Typography variant="body1" color={theme.palette.greySecondary.main} className="lead">
                                {t(`common::msg::${"Please select a project you want to access."}`)}
                            </Typography>
                        </Box>
                    </div>

                    <div className={"d-flex flex-column"}>
                        <div className={"row justify-content-center"} style={{ gap: theme.spacing(1.25) }}>
                            {accessibleStages.map((stage) => (
                                <div
                                    className={`card`}
                                    key={stage.id}
                                    style={{
                                        backgroundColor: theme.palette.background.paper,
                                        marginTop: 0,
                                        border: `1px solid ${theme.palette.greySecondary[100]}`,
                                        "&:hover": {
                                            border: `1px solid ${theme.palette.greySecondary[100]}`,
                                        },
                                    }}
                                >
                                    <div
                                        className="card-header"
                                        style={{ backgroundColor: theme.palette.background.paper }}
                                    >
                                        <i className="thumbnail">
                                            <img src={`${PUBLIC_URL}/images/stage.png`} />
                                        </i>
                                        <div className="content">
                                            <Typography variant={"h3"} color={theme.palette.greySecondary.main}>
                                                {stage.name}
                                            </Typography>
                                        </div>
                                        {(compareVersions(stage.version, VERSION_CM_5112_PROJECT_SITE_ACCESS) < 0 ||
                                            (stage.user && stage.user.isSpaceAdmin)) && (
                                            <a
                                                className="link-admin"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    onClickAdmin(stage);
                                                }}
                                            >
                                                <IconSetting
                                                    htmlColor={theme.palette.greySecondary.main}
                                                    size={16}
                                                    sx={{ mr: theme.spacing(1) }}
                                                />
                                                <Typography
                                                    variant={"body1_500"}
                                                    color={theme.palette.greySecondary.main}
                                                >
                                                    {t(`common::label::${"Administration"}`)}
                                                </Typography>
                                            </a>
                                        )}
                                    </div>
                                    <Grid mt={theme.spacing(3)}>
                                        <TextField
                                            type={"search"}
                                            fullWidth
                                            sx={{
                                                [theme.breakpoints.down("xl")]: {
                                                    width: `100%`,
                                                },
                                                "& .Mui-focused:has(input[type='search']) .MuiInputAdornment-positionStart":
                                                    {
                                                        width: 16,
                                                        height: 16,
                                                    },
                                            }}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment
                                                        position="start"
                                                        sx={{
                                                            width: `16px !important`,
                                                            height: `16px !important`,
                                                        }}
                                                    >
                                                        <IconSearch
                                                            size={16}
                                                            htmlColor={theme.palette.greySecondary.main}
                                                        />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            placeholder={i18n.t(`common::label::Search Projects`, `Search Projects`)}
                                            value={projectFilterKeyword[stage.id] ?? ""}
                                            onChange={(e) => {
                                                onHandleChangeFilterKeyword(stage.id, e.target.value);
                                            }}
                                        />
                                    </Grid>
                                    <div className={"card-body"}>
                                        {stage.pending ? (
                                            <div className={"loading"}>
                                                <i className={"loading-content"}></i>
                                            </div>
                                        ) : (
                                            <React.Fragment>
                                                {stage.error ? (
                                                    <CallMegazoneErrorMessageBox t={t} /> // 오류 - 메가존 문의 필요 화면
                                                ) : (
                                                    <React.Fragment>
                                                        {filterActiveProject(stage.id, stage.projects).length > 0 ? (
                                                            <React.Fragment>
                                                                <Typography
                                                                    variant={"body3_400"}
                                                                    color={theme.palette.greySecondary.main}
                                                                >
                                                                    {t(`common::label::${"Total"}`)}{" "}
                                                                </Typography>
                                                                <Typography
                                                                    variant={"body3_700"}
                                                                    color={theme.palette.primary.main}
                                                                >
                                                                    {
                                                                        filterActiveProject(stage.id, stage.projects)
                                                                            .length
                                                                    }
                                                                </Typography>
                                                                <div className="scroll-area">
                                                                    <List>
                                                                        {filterActiveProject(
                                                                            stage.id,
                                                                            stage.projects,
                                                                        ).map((project) => {
                                                                            const isCurrentProject =
                                                                                isPreviousSelectedProject(
                                                                                    stage,
                                                                                    project,
                                                                                );

                                                                            return (
                                                                                <ListItem
                                                                                    key={project.id}
                                                                                    sx={{
                                                                                        height: "78px",
                                                                                        py: theme.spacing(2),
                                                                                        pl: theme.spacing(2.5),
                                                                                        pr: theme.spacing(1.5),
                                                                                        borderRadius:
                                                                                            theme.spacing(0.25),
                                                                                        border: `1px solid ${theme.palette.greySecondary[100]}`,
                                                                                        backgroundColor:
                                                                                            theme.palette.background
                                                                                                .paper,
                                                                                        "& .tools": {
                                                                                            display: "none",
                                                                                        },
                                                                                        "& .chevron-icon": {
                                                                                            color: theme.palette
                                                                                                .greySecondary[500],
                                                                                        },
                                                                                        "&:hover": {
                                                                                            backgroundColor: alpha(
                                                                                                theme.palette.primary
                                                                                                    .main,
                                                                                                0.2,
                                                                                            ),
                                                                                            border: `1px solid ${theme.palette.primary.main}`,
                                                                                            cursor: "pointer",
                                                                                            "& .tools": {
                                                                                                display: "block",
                                                                                            },
                                                                                            "& .chevron-icon": {
                                                                                                color: theme.palette
                                                                                                    .primary.main,
                                                                                            },
                                                                                        },
                                                                                        "&:not(:last-child)": {
                                                                                            mb: theme.spacing(0.75),
                                                                                        },
                                                                                        ...(isCurrentProject && {
                                                                                            backgroundColor:
                                                                                                theme.palette.primary
                                                                                                    .light,
                                                                                            border: `1px solid ${theme.palette.primary.main}`,
                                                                                            cursor: "pointer",
                                                                                            "& .chevron-icon": {
                                                                                                color: theme.palette
                                                                                                    .background.paper,
                                                                                            },
                                                                                            "&:hover": {
                                                                                                backgroundColor:
                                                                                                    theme.palette
                                                                                                        .primary.light,
                                                                                                border: `1px solid ${theme.palette.primary.main}`,
                                                                                                "& .chevron-icon": {
                                                                                                    color: theme.palette
                                                                                                        .background
                                                                                                        .paper,
                                                                                                },
                                                                                                "& .tools": {
                                                                                                    display: "block",
                                                                                                },
                                                                                            },
                                                                                        }),
                                                                                    }}
                                                                                    onClick={(e) => {
                                                                                        e.preventDefault();
                                                                                        onClickProject(stage, project);
                                                                                    }}
                                                                                >
                                                                                    <Grid
                                                                                        container
                                                                                        flexDirection={"row"}
                                                                                        justifyContent={"flex-start"}
                                                                                        alignItems={"center"}
                                                                                        gap={theme.spacing(1)}
                                                                                    >
                                                                                        <Box>
                                                                                            <Avatar
                                                                                                sx={{
                                                                                                    width: 32,
                                                                                                    height: 32,
                                                                                                    fontSize: 16,
                                                                                                    fontWeight: `bold`,
                                                                                                    margin: `0 auto`,
                                                                                                    backgroundColor:
                                                                                                        getProjectRandomColor(
                                                                                                            project.id,
                                                                                                        ),
                                                                                                }}
                                                                                            >
                                                                                                {project?.name?.charAt(
                                                                                                    0,
                                                                                                )}
                                                                                            </Avatar>
                                                                                        </Box>
                                                                                        <Typography
                                                                                            variant={"body1_500"}
                                                                                            color={
                                                                                                isCurrentProject
                                                                                                    ? theme.palette
                                                                                                          .background
                                                                                                          .paper
                                                                                                    : theme.palette
                                                                                                          .greySecondary
                                                                                                          .main
                                                                                            }
                                                                                        >
                                                                                            {project.name}
                                                                                        </Typography>
                                                                                        {isCurrentProject && (
                                                                                            <Typography
                                                                                                variant={"body1_400"}
                                                                                                color={
                                                                                                    theme.palette
                                                                                                        .background
                                                                                                        .paper
                                                                                                }
                                                                                            >
                                                                                                (
                                                                                                {t(
                                                                                                    `common::label::${"Currently online"}`,
                                                                                                )}
                                                                                                )
                                                                                            </Typography>
                                                                                        )}
                                                                                        {getStoredUserData(stage.id) ===
                                                                                            project.id && (
                                                                                            <Box
                                                                                                sx={{
                                                                                                    width: "75px",
                                                                                                    height: "18px",
                                                                                                    borderRadius: "9px",
                                                                                                    backgroundColor:
                                                                                                        theme.palette
                                                                                                            .constant
                                                                                                            .black,
                                                                                                    display: "flex",
                                                                                                    flexDirection:
                                                                                                        "row",
                                                                                                    alignItems:
                                                                                                        "center",
                                                                                                    gap: theme.spacing(
                                                                                                        0.75,
                                                                                                    ),
                                                                                                    px: theme.spacing(
                                                                                                        1,
                                                                                                    ),
                                                                                                }}
                                                                                            >
                                                                                                <IconCheckDefault
                                                                                                    size={12}
                                                                                                    htmlColor={
                                                                                                        theme.palette
                                                                                                            .primary
                                                                                                            .light
                                                                                                    }
                                                                                                />
                                                                                                <Typography
                                                                                                    variant={
                                                                                                        "body3_400"
                                                                                                    }
                                                                                                    color={
                                                                                                        theme.palette
                                                                                                            .constant
                                                                                                            .white
                                                                                                    }
                                                                                                >
                                                                                                    {t(
                                                                                                        `common::label::${"Default"}`,
                                                                                                    )}
                                                                                                </Typography>
                                                                                            </Box>
                                                                                        )}
                                                                                    </Grid>
                                                                                    <div className={"tools"}>
                                                                                        <div className={"more"}>
                                                                                            <Box>
                                                                                                <Tooltip
                                                                                                    title={t(
                                                                                                        `common::label::More`,
                                                                                                        `More`,
                                                                                                    )}
                                                                                                >
                                                                                                    <IconButton
                                                                                                        variant={
                                                                                                            "outlined"
                                                                                                        }
                                                                                                        size={"small"}
                                                                                                        circled
                                                                                                        sx={{
                                                                                                            borderColor:
                                                                                                                alpha(
                                                                                                                    theme
                                                                                                                        .palette
                                                                                                                        .greySecondary
                                                                                                                        .main,
                                                                                                                    0.6,
                                                                                                                ),
                                                                                                            backgroundColor:
                                                                                                                theme
                                                                                                                    .palette
                                                                                                                    .background
                                                                                                                    .paper,
                                                                                                        }}
                                                                                                        onClick={(
                                                                                                            e,
                                                                                                        ) => {
                                                                                                            e.stopPropagation();
                                                                                                            onClickToggleActionButton(
                                                                                                                e,
                                                                                                                project.id,
                                                                                                            );
                                                                                                        }}
                                                                                                    >
                                                                                                        <IconMoreHoriz
                                                                                                            size={16}
                                                                                                            htmlColor={
                                                                                                                theme
                                                                                                                    .palette
                                                                                                                    .greySecondary
                                                                                                                    .main
                                                                                                            }
                                                                                                        />
                                                                                                    </IconButton>
                                                                                                </Tooltip>
                                                                                            </Box>
                                                                                            <Popper
                                                                                                open={
                                                                                                    Boolean(anchorEl) &&
                                                                                                    getCurrentProjectId() ===
                                                                                                        project.id
                                                                                                }
                                                                                                anchorEl={anchorEl}
                                                                                                placement="bottom-end"
                                                                                                sx={{ zIndex: 1300 }}
                                                                                            >
                                                                                                <ClickAwayListener
                                                                                                    onClickAway={
                                                                                                        handleClosePopper
                                                                                                    }
                                                                                                >
                                                                                                    <Paper
                                                                                                        sx={{
                                                                                                            border: `1px solid ${theme.palette.greySecondary[100]}`,
                                                                                                            borderRadius:
                                                                                                                theme.spacing(
                                                                                                                    0.5,
                                                                                                                ),
                                                                                                            boxShadow:
                                                                                                                "none",
                                                                                                            minWidth: 120,
                                                                                                        }}
                                                                                                    >
                                                                                                        <MenuList
                                                                                                            onClick={(
                                                                                                                e,
                                                                                                            ) =>
                                                                                                                e.stopPropagation()
                                                                                                            }
                                                                                                        >
                                                                                                            {getStoredUserData(
                                                                                                                stage.id,
                                                                                                            ) ===
                                                                                                            project.id ? (
                                                                                                                <MenuItem
                                                                                                                    onClick={() => {
                                                                                                                        onClickUnSetDefaultProject(
                                                                                                                            stage.id,
                                                                                                                        );
                                                                                                                    }}
                                                                                                                    sx={{
                                                                                                                        fontSize:
                                                                                                                            theme
                                                                                                                                .typography
                                                                                                                                .body2
                                                                                                                                .fontSize,
                                                                                                                        py: theme.spacing(
                                                                                                                            1,
                                                                                                                        ),
                                                                                                                        px: theme.spacing(
                                                                                                                            2,
                                                                                                                        ),
                                                                                                                    }}
                                                                                                                >
                                                                                                                    {t(
                                                                                                                        `common::label::${"Unset default"}`,
                                                                                                                    )}
                                                                                                                </MenuItem>
                                                                                                            ) : (
                                                                                                                <MenuItem
                                                                                                                    onClick={() => {
                                                                                                                        onClickSetDefaultProject(
                                                                                                                            stage.id,
                                                                                                                            project.id,
                                                                                                                        );
                                                                                                                    }}
                                                                                                                    sx={{
                                                                                                                        fontSize:
                                                                                                                            theme
                                                                                                                                .typography
                                                                                                                                .body2
                                                                                                                                .fontSize,
                                                                                                                        py: theme.spacing(
                                                                                                                            1,
                                                                                                                        ),
                                                                                                                        px: theme.spacing(
                                                                                                                            2,
                                                                                                                        ),
                                                                                                                    }}
                                                                                                                >
                                                                                                                    {t(
                                                                                                                        `common::label::${"Set as default"}`,
                                                                                                                    )}
                                                                                                                </MenuItem>
                                                                                                            )}
                                                                                                        </MenuList>
                                                                                                    </Paper>
                                                                                                </ClickAwayListener>
                                                                                            </Popper>
                                                                                        </div>
                                                                                    </div>
                                                                                    <Box>
                                                                                        <IconChevronRight
                                                                                            className="chevron-icon"
                                                                                            size={16}
                                                                                        />
                                                                                    </Box>
                                                                                </ListItem>
                                                                            );
                                                                        })}
                                                                    </List>
                                                                </div>
                                                            </React.Fragment>
                                                        ) : (
                                                            <NoProjectErrorMessageBox t={t} />
                                                        )}
                                                    </React.Fragment>
                                                )}
                                            </React.Fragment>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            ) : (
                <>
                    {hasCheckedAllStages && (
                        <div className={"gate-empty"}>
                            <i className={"sprite sprite-error-big"}></i>
                            <h1>{t(`common::msg::${"There is no project available to access."}`)}</h1>
                            <div className={"card"}>
                                <div className={"card-body"}>
                                    <p className={"message"}>
                                        <Trans
                                            i18nKey="common::msg::If you have a problem with your account, or would like to use the cloud <0/>Platform, please contact our sales team. We'll assist you with more details."
                                            components={[<br key="br" />]}
                                        />
                                    </p>
                                    <div className={"row"}>
                                        <div className={"col"}>
                                            <strong className={"title"}>
                                                {t(`common::label::{{name}} inquiry`, {
                                                    name: t(`common::label::${"Cloud"}`),
                                                })}
                                            </strong>
                                            <ul>
                                                <li className={"dot"}>
                                                    {t(`common::label::${"24 hour operation"}`)} :{" "}
                                                    <strong>1644-2243</strong>
                                                </li>
                                                <li className={"dot"}>
                                                    {t(`common::label::${"Email"}`)} :{" "}
                                                    <a href="mailto:cloud@megazone.com">cloud@megazone.com</a>
                                                </li>
                                            </ul>
                                        </div>
                                        <div className={"col"}>
                                            <strong className={"title"}>
                                                {t(`common::label::{{name}} inquiry`, { name: "Megazone" })}
                                            </strong>
                                            <p className={"dot"}>
                                                <a href="https://mz-cloudplex.zendesk.com">
                                                    {"https://mz-cloudplex.zendesk.com"}
                                                </a>
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </>
            )}
        </React.Fragment>
    );
};

export default Stage;
